import { Search as SearchComponent, useSearch } from '@haus-storefront-react/search'
import { AriaLiveRegion } from '@components'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchIcon } from './SearchIcon'
import { SearchPrefilledResults } from './SearchPrefilledResults'
import { SearchTypedResults } from './SearchTypedResults'
import type { SearchWidgetProps } from './types'
import styles from './Search.module.scss'

const SearchWidget = ({ configStyles, collectionId }: SearchWidgetProps) => {
  const { t } = useTranslation()
  const searchInputId = useId()
  const [searchAnnouncement, setSearchAnnouncement] = useState('')

  const effectiveCollectionId =
    collectionId != null && String(collectionId) !== '0' ? String(collectionId) : undefined

  const prefilledSearch = useSearch({
    collectionId: effectiveCollectionId,
    term: '',
    take: 5,
    groupByProduct: true,
  })
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    const term = e.currentTarget.value.trim()
    if (term) {
      setSearchAnnouncement(
        t('a11y.search.aria.loadingResults', {
          term,
        }),
      )
      e.preventDefault()
      window.location.href = t('urls.search', {
        term: encodeURIComponent(term),
      })
    }
  }

  return (
    <SearchComponent.Root term="" take={5} groupByProduct>
      {(searchContext) => {
        const searchTerm = searchContext.variables.term?.trim() ?? ''
        const isPrefilledMode = !searchTerm && !!effectiveCollectionId

        return (
          <div className={styles.searchRoot}>
            <AriaLiveRegion message={searchAnnouncement} />
            <label htmlFor={searchInputId} className={styles.searchLabel}>
              {t('searchField.searchPlaceholder')}
            </label>
            <div className={styles.inputWrapper}>
              <SearchComponent.Input
                id={searchInputId}
                disabled={false}
                placeholder={t('searchField.searchPlaceholder')}
                className={styles.searchInput}
                aria-label={t('searchField.searchPlaceholder')}
                onKeyDown={handleInputKeyDown}
              />
              <span className={styles.searchIconWrapper} aria-hidden>
                <SearchIcon />
              </span>
            </div>

            {isPrefilledMode ? (
              <SearchPrefilledResults
                configStyles={configStyles}
                error={prefilledSearch.error}
                isLoading={prefilledSearch.isLoading}
                items={prefilledSearch.data?.items ?? []}
              />
            ) : (
              <SearchTypedResults configStyles={configStyles} searchTerm={searchTerm} />
            )}
          </div>
        )
      }}
    </SearchComponent.Root>
  )
}

export default SearchWidget
